// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@flip/App/Components/Common/XngZone";
import XngSticky from "@flip/App/Components/XngSticky/XngSticky";
import { xScreen } from "@flip/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    isStickyTotal = "isStickyTotal",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.isStickyTotal]: boolean;
    };
}
export interface MyAccountDetailsPagesLayout {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    extraWidgetContainer?: InjectableCss;
    informationContainer?: InjectableCss;
    productLisContainer?: InjectableCss;
    information?: InjectableCss;
    total?: InjectableCss;
    totalEmpty?: InjectableCss;
    productList?: InjectableCss;
    xngStickyStyles?: InjectableCss;
    // XNG-Changes:
    logo?: InjectableCss;
}

export const myAccountDetailsPagesLayout: MyAccountDetailsPagesLayout = {
    container: {
        css: css`
            @media print {
                display: block !important;
            }
        `,
    },
    extraWidgetContainer: {
        css: css``,
    },
    informationContainer: {
        css: css`
            display: flex;
            gap: 24px;
            ${xScreen("lte", "xs")} {
                flex-direction: column-reverse;
            }
            @media print {
                display: block;
            }
        `,
    },
    // XNG-Change: FLIP-931
    information: {
        css: css`
            display: flex;
            flex-direction: column;
            @media print {
                display: block;
                h2 {
                    font-size: 15px;
                }
            }
            ${xScreen("lte", "xs")} {
                flex: 1 1 100%;
                width: 100%;
            }
        `,
    },
    productList: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            @media print {
                display: block;
            }
        `,
    },
    total: {
        css: css`
            flex: 0 0 319px;
            @media print {
                margin-top: 25px;
            }
            ${xScreen("lte", "md")} {
                flex-basis: 234px;
                align-self: flex-start;
            }
            ${xScreen("lte", "xs")} {
                flex-basis: 100%;
            }
        `,
    },
    totalEmpty: {
        css: css`
            flex: 0 0 319px;
            ${xScreen("lte", "md")} {
                flex-basis: 234px;
            }
            ${xScreen("lte", "xs")} {
                display: none;
            }
        `,
    },
    productLisContainer: {
        css: css`
            @media print {
                display: block;
            }
        `,
    },
    xngStickyStyles: {
        css: css`
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    logo: {
        css: css`
            a {
                display: none;
            }
            @media print {
                margin: 20px 0;
                a {
                    display: block;
                }
            }
        `,
    },
};

const styles = myAccountDetailsPagesLayout;
const MyAccountDetailsPagesLayout: FC<OwnProps> = ({ id, fields }) => {
    return (
        <StyledWrapper {...styles.container} data-test-selector="My-Account-Details-Pages-Layout">
            <StyledWrapper {...styles.logo}>
                <XngZone contentId={id} zoneName="Logo" hint="Zone for Logo (print mode)" />
            </StyledWrapper>
            <StyledWrapper {...styles.titleContainer}>
                <XngZone contentId={id} zoneName="Content01" hint="Zone For Title" />
            </StyledWrapper>
            <StyledWrapper {...styles.extraWidgetContainer}>
                <XngZone contentId={id} zoneName="Content02" hint="Zone For Extra Widgets" />
            </StyledWrapper>
            <StyledWrapper {...styles.informationContainer}>
                <StyledWrapper {...styles.productList}>
                    <StyledWrapper {...styles.information}>
                        <XngZone contentId={id} zoneName="Content03" hint="Zone For Information" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productList}>
                        <XngZone contentId={id} zoneName="Content06" hint="Zone Product List Not Full Width" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.total}>
                    {fields.isStickyTotal ? (
                        <XngSticky {...styles.xngStickyStyles}>
                            <XngZone contentId={id} zoneName="Content04" hint="Zone For Total" />
                        </XngSticky>
                    ) : (
                        <XngZone contentId={id} zoneName="Content04" hint="Zone For Total" />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.productLisContainer}>
                <XngZone contentId={id} zoneName="Content05" hint="Zone Product List" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyAccountDetailsPagesLayout,
    definition: {
        group: "Layout" as any,
        displayName: "My Account Details Pages Layout",
        fieldDefinitions: [
            {
                name: fields.isStickyTotal,
                displayName: "Is Sticky Total",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
    },
};

export default widgetModule;
